import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { getAssignments, deleteAssignment, publishAssignment } from "../../api/assignmentApi";
import { useAuth } from "../../common/contexts/AuthContext";

const statusColors = {
  draft: "warning",
  published: "success",
  closed: "secondary",
};

const AssignmentListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleName = user?.role?.name || user?.Role?.name;
  const canManage = roleName === "Admin" || roleName === "Teacher" || roleName === "SuperAdmin";

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await getAssignments();
      if (data?.success) setItems(data.items || []);
    } catch (err) {
      console.error('Failed to load assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = (row) => {
    confirmDialog({
      message: `Delete assignment "${row.title}"?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        await deleteAssignment(row.id);
        refresh();
      },
    });
  };

  const handlePublish = async (row) => {
    await publishAssignment(row.id);
    refresh();
  };

  const statusTemplate = (row) => (
    <Tag value={row.status} severity={statusColors[row.status] || "info"} />
  );

  const dueDateTemplate = (row) =>
    row.due_date ? new Date(row.due_date).toLocaleDateString() : "—";

  const actionsTemplate = (row) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-rounded p-button-sm"
        onClick={() => navigate(`/index/assignments/${row.id}`)}
        tooltip="View"
      />
      {canManage && (
        <>
          <Button
            icon="pi pi-pencil"
            className="p-button-text p-button-rounded p-button-sm"
            onClick={() => navigate(`/index/assignments/${row.id}/edit`)}
            tooltip="Edit"
          />
          {row.status === "draft" && (
            <Button
              icon="pi pi-send"
              className="p-button-text p-button-rounded p-button-sm p-button-success"
              onClick={() => handlePublish(row)}
              tooltip="Publish"
            />
          )}
          <Button
            icon="pi pi-trash"
            className="p-button-text p-button-rounded p-button-sm p-button-danger"
            onClick={() => handleDelete(row)}
            tooltip="Delete"
          />
        </>
      )}
    </div>
  );

  return (
    <Card>
      <ConfirmDialog />
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">Assignments</h2>
        {canManage && (
          <Button
            label="New Assignment"
            icon="pi pi-plus"
            onClick={() => navigate("/index/assignments/new")}
          />
        )}
      </div>
      <DataTable
        value={items}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="No assignments found."
        stripedRows
      >
        <Column field="title" header="Title" sortable />
        <Column field="grade.name" header="Grade" />
        <Column field="subject.name" header="Subject" />
        <Column field="due_date" header="Due Date" body={dueDateTemplate} sortable />
        <Column field="max_score" header="Max Score" />
        <Column field="status" header="Status" body={statusTemplate} sortable />
        <Column header="Actions" body={actionsTemplate} style={{ width: "200px" }} />
      </DataTable>
    </Card>
  );
};

export default AssignmentListPage;
